'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, VectorizeResponse } from '@/lib/ai-models-api';

export default function VectorizePage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Vectorization parameters
  const [colormode, setColormode] = useState<'color' | 'binary'>('color');
  const [hierarchical, setHierarchical] = useState<'stacked' | 'cutout'>('stacked');
  const [filterSpeckle, setFilterSpeckle] = useState(4);
  const [colorPrecision, setColorPrecision] = useState(6);
  const [layerDifference, setLayerDifference] = useState(16);
  const [cornerThreshold, setCornerThreshold] = useState(60);
  const [lengthThreshold, setLengthThreshold] = useState(4.0);
  const [spliceThreshold, setSpliceThreshold] = useState(45);
  const [pathPrecision, setPathPrecision] = useState(3);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VectorizeResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      const response = await aiModelsApi.vectorizeConvert(image, {
        colormode,
        hierarchical,
        filter_speckle: filterSpeckle,
        color_precision: colorPrecision,
        layer_difference: layerDifference,
        corner_threshold: cornerThreshold,
        length_threshold: lengthThreshold,
        splice_threshold: spliceThreshold,
        path_precision: pathPrecision,
      });
      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Make SVG responsive by adding viewBox and setting width to 100%
  const getResponsiveSvg = (svg: string) => {
    // Extract width and height values
    const widthMatch = svg.match(/width="(\d+)"/);
    const heightMatch = svg.match(/height="(\d+)"/);

    if (widthMatch && heightMatch) {
      const width = widthMatch[1];
      const height = heightMatch[1];

      // Add viewBox if not present
      let result = svg;
      if (!svg.includes('viewBox')) {
        result = result.replace(
          /<svg([^>]*)>/,
          `<svg$1 viewBox="0 0 ${width} ${height}">`
        );
      }

      // Replace fixed dimensions with responsive ones
      result = result
        .replace(/width="\d+"/, 'width="100%"')
        .replace(/height="\d+"/, 'height="auto"');

      return result;
    }
    return svg;
  };

  const downloadSvg = () => {
    if (!result) return;
    const blob = new Blob([result.svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vectorized.svg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">Vectorize - Image to SVG Conversion</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Color Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Color Mode</label>
                  <select
                    value={colormode}
                    onChange={(e) => setColormode(e.target.value as 'color' | 'binary')}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="color">Color (Multi-color output)</option>
                    <option value="binary">Binary (Black & white)</option>
                  </select>
                </div>

                {/* Hierarchical Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Layer Hierarchy</label>
                  <select
                    value={hierarchical}
                    onChange={(e) => setHierarchical(e.target.value as 'stacked' | 'cutout')}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="stacked">Stacked (Layers on top)</option>
                    <option value="cutout">Cutout (Holes in shapes)</option>
                  </select>
                </div>

                {/* Filter Speckle */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Filter Speckle: {filterSpeckle}px
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={filterSpeckle}
                    onChange={(e) => setFilterSpeckle(Number(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">Remove small speckles</p>
                </div>

                {/* Color Precision (only for color mode) */}
                {colormode === 'color' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Color Precision: {colorPrecision}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={colorPrecision}
                      onChange={(e) => setColorPrecision(Number(e.target.value))}
                      className="w-full accent-[var(--accent)]"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">Higher = more colors</p>
                  </div>
                )}

                {/* Layer Difference (only for color mode) */}
                {colormode === 'color' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Layer Difference: {layerDifference}
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="255"
                      value={layerDifference}
                      onChange={(e) => setLayerDifference(Number(e.target.value))}
                      className="w-full accent-[var(--accent)]"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">Layer grouping sensitivity</p>
                  </div>
                )}

                {/* Corner Threshold */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Corner Threshold: {cornerThreshold}°
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="180"
                    value={cornerThreshold}
                    onChange={(e) => setCornerThreshold(Number(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">Corner detection angle</p>
                </div>

                {/* Length Threshold */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Length Threshold: {lengthThreshold.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="0.5"
                    value={lengthThreshold}
                    onChange={(e) => setLengthThreshold(Number(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">Minimum line length</p>
                </div>

                {/* Splice Threshold */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Splice Threshold: {spliceThreshold}°
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="180"
                    value={spliceThreshold}
                    onChange={(e) => setSpliceThreshold(Number(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">Path joining angle</p>
                </div>

                {/* Path Precision */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Path Precision: {pathPrecision}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={pathPrecision}
                    onChange={(e) => setPathPrecision(Number(e.target.value))}
                    className="w-full accent-[var(--accent)]"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">SVG path decimal precision</p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Vectorizing...' : 'Vectorize Image'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3m7.5-12.99l.75-1.3m-6.063 16.658l.26-1.477m2.605-14.772l.26-1.477m0 17.726l-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205L12 12m0 0l4.5 7.795" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image to convert to SVG vector</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Vectorizing image...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Vectorized SVG Preview */}
                    <div>
                      <p className="text-sm text-[var(--text-muted)] mb-2">Vectorized (SVG)</p>
                      <div
                        className="w-full rounded-lg bg-white overflow-hidden"
                        dangerouslySetInnerHTML={{ __html: getResponsiveSvg(result.svg) }}
                      />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase">Inference Time</p>
                        <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                      </div>
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase">SVG Size</p>
                        <p className="text-lg font-semibold">{formatBytes(result.svg_size_bytes)}</p>
                      </div>
                      <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                        <p className="text-xs text-[var(--text-muted)] uppercase">Original Size</p>
                        <p className="text-lg font-semibold">{result.image_size[0]}x{result.image_size[1]}</p>
                      </div>
                    </div>

                    {/* Mode Info */}
                    <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div className="flex gap-4">
                        <div>
                          <p className="text-xs text-[var(--text-muted)] uppercase">Color Mode</p>
                          <p className="font-medium capitalize">{result.colormode}</p>
                        </div>
                        <div>
                          <p className="text-xs text-[var(--text-muted)] uppercase">Hierarchy</p>
                          <p className="font-medium capitalize">{result.hierarchical}</p>
                        </div>
                      </div>
                    </div>

                    {/* Download */}
                    <button
                      onClick={downloadSvg}
                      className="block w-full py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                    >
                      Download SVG
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
